import React, { useState, useEffect } from 'react';
import '../assets/AIInsights.css';
import AITimeSeriesChart from '../aiInsights/charts/AITimeSeriesChart';
import AIDistributionChart from '../aiInsights/charts/AIDistributionChart';
import AIPercentileChart from '../aiInsights/charts/AIPercentileChart';
import AIDataTable from '../aiInsights/charts/AIDataTable';

async function readErrorPayload(response) {
  try {
    const body = await response.json();
    const detail = body && body.detail;
    if (detail && typeof detail === 'object') {
      return { ...body, ...detail };
    }
    if (typeof detail === 'string') {
      return { ...body, message: body.message || detail };
    }
    return body || {};
  } catch (_) {
    return {};
  }
}

function AIInsights({ userInfo, role }) {
  // View type and input state
  const [viewType, setViewType] = useState('aggregate'); // 'device', 'device-tag', 'aggregate'
  const [inputValue, setInputValue] = useState(''); // device ID or tag name
  const [timeRange, setTimeRange] = useState(7); // 1 or 7 days

  const [availableTags, setAvailableTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  
  const [showInsights, setShowInsights] = useState(false);
  const [activeTab, setActiveTab] = useState('data-stall');
  
  // API data state
  const [peakRateULData, setPeakRateULData] = useState(null);
  const [peakRateDLData, setPeakRateDLData] = useState(null);
  const [dataStallData, setDataStallData] = useState(null);
  const [thermalData, setThermalData] = useState(null);

  // Individual loading states for progressive loading
  const [dataStallLoading, setDataStallLoading] = useState(false);
  const [peakRateULLoading, setPeakRateULLoading] = useState(false);
  const [peakRateDLLoading, setPeakRateDLLoading] = useState(false);
  const [thermalLoading, setThermalLoading] = useState(false);

  // Individual error states
  const [peakRateULError, setPeakRateULError] = useState(null);
  const [peakRateDLError, setPeakRateDLError] = useState(null);
  const [thermalError, setThermalError] = useState(null);
  const [dataStallError, setDataStallError] = useState(null);

  const [crashData, setCrashData] = useState(null);
  const [crashLoading, setCrashLoading] = useState(false);
  const [crashError, setCrashError] = useState(null);

  // Cache metadata state
  const [cacheMetadata, setCacheMetadata] = useState({
    dataStall: null,
    peakRateUL: null,
    peakRateDL: null,
    thermal: null,
    crash: null
  });

  // Device count per insight type
  const [deviceCounts, setDeviceCounts] = useState({
    dataStall: null,
    peakRateUL: null,
    peakRateDL: null,
    thermal: null,
    crash: null
  });

  // Retry tracking
  const [retryAttempts, setRetryAttempts] = useState({
    dataStall: 0,
    peakRateUL: 0,
    peakRateDL: 0,
    thermal: 0,
    crash: 0
  });

  // Legacy loading state for initial fetch
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState(null);

  const MAX_RETRY_ATTEMPTS = 3;

  // Fetch available device tags when device-tag view is selected
  useEffect(() => {
    if (viewType !== 'device-tag') return;
    const apiBaseUrl = (process.env.REACT_APP_WBC_API_BASE_URL || 'http://localhost:8080').replace(/\/$/, '');
    const queryServicePath = (process.env.REACT_APP_WBC_QUERY_SERVICE_PATH || '/queryService/api/v1');
    const path = queryServicePath.startsWith('/') ? queryServicePath : `/${queryServicePath}`;
    setTagsLoading(true);
    setInputValue('');
    fetch(`${apiBaseUrl}${path}/device-tags`)
      .then(r => r.json())
      .then(data => setAvailableTags(data.tags || []))
      .catch(() => setAvailableTags([]))
      .finally(() => setTagsLoading(false));
  }, [viewType]);

  // Helper function to format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const cached = new Date(timestamp);
    const diffMs = now - cached;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  // Helper function to format date range
  const formatDateRange = (timeRange) => {
    if (!timeRange || !timeRange.from || !timeRange.to) return '';
    
    const from = new Date(timeRange.from);
    const to = new Date(timeRange.to);
    
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${from.toLocaleDateString('en-US', options)} - ${to.toLocaleDateString('en-US', options)}`;
  };

  // Build URL based on view type
  const buildInsightUrl = (apiVersion, insightType, testType = null) => {
    const baseUrl = process.env.REACT_APP_WBC_AI_INSIGHTS_URL;
    const testTypeSuffix = testType ? `&test_type=${testType}` : '';

    switch (viewType) {
      case 'device':
        return `${baseUrl}/api/${apiVersion}/insights/${insightType}?device_id=${inputValue}&days=${timeRange}${testTypeSuffix}`;
      case 'device-tag':
        return `${baseUrl}/api/${apiVersion}/insights/device-tag/${inputValue}/${insightType}?days=${timeRange}${testTypeSuffix}`;
      case 'aggregate':
        return `${baseUrl}/api/${apiVersion}/insights/aggregate/${insightType}?days=${timeRange}${testTypeSuffix}`;
      default:
        return `${baseUrl}/api/${apiVersion}/insights/${insightType}?device_id=${inputValue}&days=${timeRange}${testTypeSuffix}`;
    }
  };

  // Helper function to handle fetch with proper error handling
  const fetchWithErrorHandling = async (url, insightType, setData, setError, setLoading, setMetadata, retryKey, setDeviceCount = null, token = null) => {
    const currentAttempt = retryAttempts[retryKey];
    
    try {
      setLoading(true);
      setError(null);
      console.log(`Fetching ${insightType} from:`, url);

      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, { headers });
      
      // Handle different HTTP status codes per ERROR_RESPONSE_FORMATS.md
      switch (response.status) {
        case 200: {
          // Success - parse and set data
          const data = await response.json();
          console.log(`${insightType} Full Response:`, data);
          
          // Set dashboard data (insights contains the cards directly)
          setData(data.insights);

          // Store device count
          if (setDeviceCount && data.device_count != null) {
            setDeviceCount(data.device_count);
          }

          // Store cache metadata
          if (data.cache_metadata) {
            setMetadata(data.cache_metadata);
            
            // Show warnings if data is stale
            if (data.cache_metadata.is_stale) {
              console.warn(`${insightType}: Data may be outdated. Refresh in progress.`);
            }
            
            if (data.cache_metadata.warning) {
              console.warn(`${insightType}: ${data.cache_metadata.warning}`);
            }
          } else if (data.data_status) {
            setMetadata({
              status: 'ready',
              data_status: data.data_status,
              warning: data.data_status === 'no_data' ? 'No source data is available for this window.' : null,
            });
          }
          
          // Reset retry count on success
          setRetryAttempts(prev => ({ ...prev, [retryKey]: 0 }));
          break;
        }
        
        case 202: {
          // Cache warming - auto-retry
          const errorData = await readErrorPayload(response);
          setData(null);
          const warmingMessage = errorData.message || 'Insights are still being generated.';
          console.log(`${insightType}: Cache warming -`, warmingMessage);
          
          if (currentAttempt < MAX_RETRY_ATTEMPTS) {
            const retryAfter = errorData.retry_after || 30;
            setError({
              type: 'warming',
              message: `${warmingMessage} (Attempt ${currentAttempt + 1}/${MAX_RETRY_ATTEMPTS})`,
              retryAfter
            });
            
            // Increment retry count
            setRetryAttempts(prev => ({ ...prev, [retryKey]: currentAttempt + 1 }));
            
            // Auto-retry after delay
            setTimeout(() => {
              const apiVersion = process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2';
              const retryUrl = buildInsightUrl(apiVersion, insightType);
              fetchWithErrorHandling(retryUrl, insightType, setData, setError, setLoading, setMetadata, retryKey, setDeviceCount);
            }, retryAfter * 1000);
          } else {
            setError({
              type: 'timeout',
              message: 'Insights generation timed out. Please try again later.'
            });
            setRetryAttempts(prev => ({ ...prev, [retryKey]: 0 }));
          }
          break;
        }
        
        case 404: {
          // Not found
          const errorData = await readErrorPayload(response);
          setData(null);
          console.error(`${insightType}: Not found -`, errorData.message);
          setError({
            type: 'not_found',
            message: errorData.message || `${viewType === 'device-tag' ? 'Device tag' : 'Resource'} not found`,
            details: errorData.details
          });
          setRetryAttempts(prev => ({ ...prev, [retryKey]: 0 }));
          break;
        }

        case 429: {
          const errorData = await readErrorPayload(response);
          setData(null);
          setError({
            type: 'rate_limit',
            message: errorData.message || 'The insights service is rate limited. Please try again later.',
            warning: errorData.warning,
            retryAfter: errorData.retry_after || 60,
          });
          setRetryAttempts(prev => ({ ...prev, [retryKey]: 0 }));
          break;
        }

        case 502:
        case 503: {
          const errorData = await readErrorPayload(response);
          setData(null);
          setError({
            type: 'unavailable',
            message: errorData.message || `Insights service is temporarily unavailable (HTTP ${response.status}).`,
            warning: errorData.warning,
            details: errorData.details,
          });
          setRetryAttempts(prev => ({ ...prev, [retryKey]: 0 }));
          break;
        }
        
        case 500: {
          // Generation failed
          const errorData = await readErrorPayload(response);
          console.error(`${insightType}: Generation failed -`, errorData.message);
          setData(null);
          setError({
            type: 'failed',
            message: errorData.message || 'Failed to generate insights',
            warning: errorData.warning,
            retryAfter: errorData.retry_after || 300
          });
          setRetryAttempts(prev => ({ ...prev, [retryKey]: 0 }));
          break;
        }
        
        default: {
          // Unexpected error
          console.error(`${insightType}: Unexpected HTTP status ${response.status}`);
          const errorData = await readErrorPayload(response);
          setData(null);
          setError({
            type: 'unexpected',
            message: errorData.message || `Unexpected error (HTTP ${response.status}). Please try again.`
          });
          setRetryAttempts(prev => ({ ...prev, [retryKey]: 0 }));
        }
      }
    } catch (err) {
      // Network error
      console.error(`${insightType}: Network error -`, err.message);
      setError({
        type: 'network',
        message: 'Network error. Please check your connection and try again.'
      });
      setRetryAttempts(prev => ({ ...prev, [retryKey]: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const fetchDataStall = (apiVersion, token) => {
    const dataStallUrl = buildInsightUrl(apiVersion, 'data-stall');
    fetchWithErrorHandling(
      dataStallUrl,
      'data-stall',
      setDataStallData,
      setDataStallError,
      setDataStallLoading,
      (metadata) => setCacheMetadata(prev => ({ ...prev, dataStall: metadata })),
      'dataStall',
      (count) => setDeviceCounts(prev => ({ ...prev, dataStall: count })),
      token
    );
  };

  const fetchPeakRateUL = (apiVersion, token) => {
    const url = buildInsightUrl(apiVersion, 'peak-rate', 'UPLINK');
    fetchWithErrorHandling(
      url,
      'peak-rate-ul',
      setPeakRateULData,
      setPeakRateULError,
      setPeakRateULLoading,
      (metadata) => setCacheMetadata(prev => ({ ...prev, peakRateUL: metadata })),
      'peakRateUL',
      (count) => setDeviceCounts(prev => ({ ...prev, peakRateUL: count })),
      token
    );
  };

  const fetchPeakRateDL = (apiVersion, token) => {
    const url = buildInsightUrl(apiVersion, 'peak-rate', 'DOWNLINK');
    fetchWithErrorHandling(
      url,
      'peak-rate-dl',
      setPeakRateDLData,
      setPeakRateDLError,
      setPeakRateDLLoading,
      (metadata) => setCacheMetadata(prev => ({ ...prev, peakRateDL: metadata })),
      'peakRateDL',
      (count) => setDeviceCounts(prev => ({ ...prev, peakRateDL: count })),
      token
    );
  };

  const fetchThermal = (apiVersion, token) => {
    const thermalUrl = buildInsightUrl(apiVersion, 'thermal');
    fetchWithErrorHandling(
      thermalUrl,
      'thermal',
      setThermalData,
      setThermalError,
      setThermalLoading,
      (metadata) => setCacheMetadata(prev => ({ ...prev, thermal: metadata })),
      'thermal',
      (count) => setDeviceCounts(prev => ({ ...prev, thermal: count })),
      token
    );
  };

  const fetchCrash = (apiVersion, token) => {
    const crashUrl = buildInsightUrl(apiVersion, 'crashes');
    fetchWithErrorHandling(
      crashUrl,
      'crashes',
      setCrashData,
      setCrashError,
      setCrashLoading,
      (metadata) => setCacheMetadata(prev => ({ ...prev, crash: metadata })),
      'crash',
      (count) => setDeviceCounts(prev => ({ ...prev, crash: count })),
      token
    );
  };

  // Fetch insights data from APIs with progressive loading
  const fetchInsightsData = async () => {
    const apiVersion = process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2';

    // Reset states
    setLoading(true);
    setShowInsights(true);
    setError(null);
    setActiveTab('data-stall');
    setPeakRateULData(null);
    setPeakRateDLData(null);
    setDataStallData(null);
    setThermalData(null);
    setCrashData(null);
    setPeakRateULError(null);
    setPeakRateDLError(null);
    setThermalError(null);
    setDataStallError(null);
    setCrashError(null);
    setCacheMetadata({ dataStall: null, peakRateUL: null, peakRateDL: null, thermal: null, crash: null });
    setDeviceCounts({ dataStall: null, peakRateUL: null, peakRateDL: null, thermal: null, crash: null });

    // Fetch APIGEE token before calling AI Insights service
    let apigeeToken = null;
    try {
      const tokenUrl = process.env.REACT_APP_WBC_API_BASE_URL + '/api/v1/apigee/token';
      const tokenResponse = await fetch(tokenUrl);
      if (tokenResponse.ok) {
        const tokenData = await tokenResponse.json();
        apigeeToken = tokenData.access_token;
      } else {
        console.warn('Failed to fetch APIGEE token, proceeding without authorization header');
      }
    } catch (err) {
      console.warn('Error fetching APIGEE token:', err.message);
    }

    // Fetch all insights in parallel, passing the token
    fetchDataStall(apiVersion, apigeeToken);
    fetchPeakRateUL(apiVersion, apigeeToken);
    fetchPeakRateDL(apiVersion, apigeeToken);
    fetchThermal(apiVersion, apigeeToken);
    fetchCrash(apiVersion, apigeeToken);

    // Remove the main loading spinner after a short delay
    setTimeout(() => setLoading(false), 1000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate input based on view type
    if (viewType !== 'aggregate' && !inputValue.trim()) {
      setError(`Please enter a ${viewType === 'device' ? 'System ID' : 'Device Tag'}`);
      return;
    }
    
    setShowInsights(true);
    fetchInsightsData();
  };

  const renderMetricsCard = (card, icon, title, iconColor) => {
    if (!card || !card.primary_metric || !card.secondary_metric || !card.range_metric || !card.count_metric) {
      return null;
    }
    return (
      <div className="ai-card">
        <div className="ai-card-header">
          <div className={`ai-card-icon ${iconColor}`}>{icon}</div>
          <div className="ai-card-title">{title}</div>
        </div>
        <div className="ai-metrics-stats">
          <div className="ai-stat-item">
            <span className={`ai-stat-value ${card.primary_metric.color}`}>{card.primary_metric.value}</span>
            <span className="ai-stat-label">{card.primary_metric.label}</span>
          </div>
          <div className="ai-stat-item">
            <span className={`ai-stat-value ${card.secondary_metric.color}`}>{card.secondary_metric.value}</span>
            <span className="ai-stat-label">{card.secondary_metric.label}</span>
          </div>
          <div className="ai-stat-item">
            <span className={`ai-stat-value ${card.range_metric.color}`}>{card.range_metric.value}</span>
            <span className="ai-stat-label">{card.range_metric.label}</span>
          </div>
          <div className="ai-stat-item">
            <span className={`ai-stat-value ${card.count_metric.color}`}>{card.count_metric.value}</span>
            <span className="ai-stat-label">{card.count_metric.label}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderExtremesCard = (card) => {
    if (!card || !card.items) {
      return null;
    }
    return (
      <div className="ai-card">
        <div className="ai-card-header">
          <div className="ai-card-icon warning-bg">{card.icon}</div>
          <div className="ai-card-title">{card.title}</div>
        </div>
        <ul className="ai-item-list">
          {card.items.map((item, index) => (
            <li key={index} className="ai-item">
              <div className={`ai-item-bullet ${item.indicator_color}-bg`}></div>
              <div className="ai-item-content">
                <div className="ai-item-label">
                  {item.label}
                  <span className="ai-item-value">{item.value}</span>
                </div>
                <div className="ai-item-details">{item.details}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderAnomaliesCard = (card) => {
    if (!card || !card.items) {
      return null;
    }
    return (
      <div className="ai-card">
        <div className="ai-card-header">
          <div className="ai-card-icon danger-bg">{card.icon}</div>
          <div className="ai-card-title">{card.title}</div>
        </div>
        <ul className="ai-item-list">
          {card.items.map((item, index) => (
            <li key={index} className="ai-item">
              <div className={`ai-item-bullet ${item.indicator_color}-bg`}></div>
              <div className="ai-item-content">
                <div className="ai-item-label">
                  {item.label}
                  <span className="ai-item-value">{item.value}</span>
                </div>
                <div className="ai-item-details">{item.description}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderTrendsCard = (card) => {
    if (!card) {
      return null;
    }

    const { chart_type, chart_data, chart_labels, table_data, icon, title, trend_description, details } = card;

    // Render based on chart_type
    switch (chart_type) {
      case 'time-series':
        return (
          <div className="ai-card">
            <div className="ai-card-header">
              <div className="ai-card-icon primary-bg">{icon}</div>
              <div className="ai-card-title">{title}</div>
            </div>
            <div className="ai-trend-description">
              <strong>Trend:</strong> {trend_description}
            </div>
            <div className="ai-trend-details">{details}</div>
            <AITimeSeriesChart
              title=""
              chartData={chart_data}
              chartLabels={chart_labels}
              yAxisLabel="Value"
            />
          </div>
        );

      case 'distribution':
        return (
          <div className="ai-card">
            <div className="ai-card-header">
              <div className="ai-card-icon primary-bg">{icon}</div>
              <div className="ai-card-title">{title}</div>
            </div>
            <div className="ai-trend-description">
              <strong>Trend:</strong> {trend_description}
            </div>
            <div className="ai-trend-details">{details}</div>
            <AIDistributionChart
              title=""
              chartData={chart_data}
              chartType="auto"
            />
          </div>
        );

      case 'percentile':
        return (
          <div className="ai-card">
            <div className="ai-card-header">
              <div className="ai-card-icon primary-bg">{icon}</div>
              <div className="ai-card-title">{title}</div>
            </div>
            <div className="ai-trend-description">
              <strong>Trend:</strong> {trend_description}
            </div>
            <div className="ai-trend-details">{details}</div>
            <AIPercentileChart
              title=""
              chartData={chart_data}
            />
          </div>
        );

      case 'table':
        return (
          <div className="ai-card">
            <div className="ai-card-header">
              <div className="ai-card-icon primary-bg">{icon}</div>
              <div className="ai-card-title">{title}</div>
            </div>
            <div className="ai-trend-description">
              <strong>Trend:</strong> {trend_description}
            </div>
            <div className="ai-trend-details">{details}</div>
            <AIDataTable
              title=""
              tableData={table_data}
            />
          </div>
        );

      case 'none':
        return (
          <div className="ai-card">
            <div className="ai-card-header">
              <div className="ai-card-icon primary-bg">{icon}</div>
              <div className="ai-card-title">{title}</div>
            </div>
            <div className="ai-trend-description">
              <strong>Trend:</strong> {trend_description}
            </div>
            <div className="ai-trend-details">{details}</div>
          </div>
        );

      default:
        // Fallback to old rendering for backward compatibility
        console.warn(`Unknown chart_type: ${chart_type}, using fallback rendering`);
        const hasChartData = chart_data && Array.isArray(chart_data) && chart_data.length > 0;
        
        // Check if it's old format (flat array) or new format (array of objects)
        const isOldFormat = hasChartData && typeof chart_data[0] === 'number';
        
        if (isOldFormat) {
          // Old format - render simple bars
          const maxValue = Math.max(...chart_data);
          return (
            <div className="ai-card">
              <div className="ai-card-header">
                <div className="ai-card-icon primary-bg">{icon}</div>
                <div className="ai-card-title">{title}</div>
              </div>
              <div className="ai-trend-description">
                <strong>Trend:</strong> {trend_description}
              </div>
              <div className="ai-trend-details">{details}</div>
              <div className="ai-chart-container">
                {chart_data.slice(0, 100).map((value, index) => {
                  const height = (value / maxValue) * 150;
                  const label = chart_labels && chart_labels[index] ? chart_labels[index] : '';
                  return (
                    <div
                      key={index}
                      className="ai-chart-bar"
                      style={{ height: `${height}px` }}
                      title={`${value}${label ? ' at ' + label : ''}`}
                    ></div>
                  );
                })}
              </div>
            </div>
          );
        } else {
          // New format but unknown type - try time-series as default
          return (
            <div className="ai-card">
              <div className="ai-card-header">
                <div className="ai-card-icon primary-bg">{icon}</div>
                <div className="ai-card-title">{title}</div>
              </div>
              <div className="ai-trend-description">
                <strong>Trend:</strong> {trend_description}
              </div>
              <div className="ai-trend-details">{details}</div>
              <AITimeSeriesChart
                title=""
                chartData={chart_data}
                chartLabels={chart_labels}
                yAxisLabel="Value"
              />
            </div>
          );
        }
    }
  };

  const renderPatternsCard = (card) => {
    if (!card || !card.items) {
      return null;
    }
    return (
      <div className="ai-card">
        <div className="ai-card-header">
          <div className="ai-card-icon warning-bg">{card.icon}</div>
          <div className="ai-card-title">{card.title}</div>
        </div>
        <ul className="ai-item-list">
          {card.items.map((item, index) => (
            <li key={index} className="ai-item">
              <div className={`ai-item-bullet ${item.indicator_color}-bg`}></div>
              <div className="ai-item-content">
                <div className="ai-item-label">
                  {item.label}
                  <span className="ai-item-value">{item.value}</span>
                </div>
                <div className="ai-item-details">{item.description}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderBaselineCard = (card) => {
    if (!card || !card.primary_stat || !card.secondary_stat) {
      return null;
    }
    return (
      <div className="ai-card">
        <div className="ai-card-header">
          <div className="ai-card-icon primary-bg">{card.icon}</div>
          <div className="ai-card-title">{card.title}</div>
        </div>
        <div className="ai-baseline-range">
          <strong>Normal Operating Range:</strong> {card.normal_operating_range}
        </div>
        <div className="ai-baseline-description">{card.range_description}</div>
        <div className="ai-baseline-stats">
          <div className="ai-stat-item">
            <span className={`ai-stat-value ${card.primary_stat.color}`}>{card.primary_stat.value}</span>
            <span className="ai-stat-label">{card.primary_stat.label}</span>
          </div>
          <div className="ai-stat-item">
            <span className={`ai-stat-value ${card.secondary_stat.color}`}>{card.secondary_stat.value}</span>
            <span className="ai-stat-label">{card.secondary_stat.label}</span>
          </div>
        </div>
      </div>
    );
  };

  // Render cache metadata info banner
  const renderCacheInfo = (metadata, deviceCount = null) => {
    if (!metadata && deviceCount == null) return null;

    const deviceChip = deviceCount != null ? (
      <span className="ai-device-count-chip">
        {deviceCount} device{deviceCount !== 1 ? 's' : ''}
      </span>
    ) : null;

    const { cached_at, data_time_range, is_stale, status, data_status, warning } = metadata || {};
    const noData = data_status === 'no_data';
    const stale = is_stale || status === 'stale';
    const statusLabel = noData
      ? 'No source data'
      : stale
        ? 'Stale data'
        : status === 'warming'
          ? 'Generating insights'
          : status === 'error'
            ? 'Cache error'
            : 'Cached data';
    const statusClass = noData
      ? 'no-data'
      : status === 'error'
        ? 'error'
        : status === 'ready' && !stale
          ? 'ready'
          : 'warning';

    // For device-specific view, show real-time data message instead of cache info
    if (viewType === 'device') {
      return (
        <div className="ai-cache-info">
          <div className="ai-cache-info-content">
            <div className="ai-cache-info-left">
              <span className={`ai-cache-status-dot ${noData ? 'no-data' : 'ready'}`}></span>
              <span className="ai-cache-info-text">
                <strong>{noData ? 'No source data' : 'Real-time Data'}</strong>
              </span>
              {deviceChip}
            </div>
            {(warning || noData) && (
              <div className="ai-cache-info-right">
                <span className="ai-cache-warning">
                  {noData ? 'No source data was returned for the selected window.' : `⚠️ ${warning}`}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (!metadata) {
      // Only device count available (no cache metadata yet)
      return deviceChip ? (
        <div className="ai-cache-info">
          <div className="ai-cache-info-content">
            <div className="ai-cache-info-left">{deviceChip}</div>
          </div>
        </div>
      ) : null;
    }

    // For device-tag and aggregate views, show cache metadata
    return (
      <div className={`ai-cache-info ${stale ? 'stale' : ''} ${noData ? 'no-data' : ''}`}>
        <div className="ai-cache-info-content">
          <div className="ai-cache-info-left">
            <span className={`ai-cache-status-dot ${statusClass}`}></span>
            <span className="ai-cache-info-text">
              <strong>{statusLabel}</strong>{cached_at ? ` · ${formatRelativeTime(cached_at)}` : ''}
            </span>
            {data_time_range && (
              <span className="ai-cache-info-text ai-cache-info-separator">
                <strong>Data Range:</strong> {formatDateRange(data_time_range)}
              </span>
            )}
            {deviceChip}
          </div>
          {(stale || warning || noData) && (
            <div className="ai-cache-info-right">
              {noData && (
                <span className="ai-cache-no-data">
                  No source data for this time range; values are shown as 0.
                </span>
              )}
              {stale && !noData && (
                <span className="ai-cache-warning">
                  ⚠️ Data is stale; the latest refresh did not complete.
                </span>
              )}
              {warning && !noData && (
                <span className="ai-cache-warning">
                  ⚠️ {warning}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRecoveryHierarchyCard = () => {
    const recoveryLevels = [
      { level: 0, name: 'NO_ACTION', description: 'No recovery attempted', severity: 'success' },
      { level: 1, name: 'MODEM_RLF', description: 'Radio Link Failure recovery - least invasive', severity: 'success' },
      { level: 2, name: 'PDN_TOGGLE', description: 'Packet Data Network reset - moderate', severity: 'warning' },
      { level: 3, name: 'LPM_TOGGLE', description: 'Low Power Mode cycle - moderate', severity: 'warning' },
      { level: 4, name: 'MODEM_SSR', description: 'Modem Subsystem Restart - aggressive', severity: 'danger' },
      { level: 5, name: 'DEVICE_REBOOT', description: 'Full device restart - very aggressive', severity: 'danger' },
      { level: 6, name: 'FACTORY_RESET', description: 'Complete device reset - most extreme', severity: 'danger' }
    ];

    return (
      <div className="ai-card">
        <div className="ai-card-header">
          <div className="ai-card-icon primary-bg">🔄</div>
          <div className="ai-card-title">Recovery Action Hierarchy</div>
        </div>
        <ol className="ai-recovery-list">
          {recoveryLevels.map((item, index) => (
            <li key={index} className="ai-recovery-item">
              <div className={`ai-recovery-bullet ${item.severity}-bg`}></div>
              <div className="ai-recovery-content">
                <div className="ai-recovery-label">
                  {item.name}
                  <span className="ai-recovery-level">(Level {item.level})</span>
                </div>
                <div className="ai-recovery-description">{item.description}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    );
  };

  return (
    <div className="ai-insights-container">
      <div className="ai-input-section">
        <div className="ai-header-content">
          <h1 className="ai-main-title">AI-Powered Insights for WBC Devices</h1>
          <h2 className="ai-subtitle">Intelligent Monitoring for Data Stalls, Throughput, Thermal Issues, and Crashes</h2>
        </div>
        <form onSubmit={handleSubmit} className="ai-device-form">
          {/* View Type Selector */}
          <div className="ai-view-selector">
            <label className="ai-view-label">View Type:</label>
            <div className="ai-view-options">
              <button
                type="button"
                className={`ai-view-option ${viewType === 'device' ? 'active' : ''}`}
                onClick={() => {
                  setViewType('device');
                  setInputValue('');
                }}
                disabled={loading}
              >
                📱 Device
              </button>
              <button
                type="button"
                className={`ai-view-option ${viewType === 'device-tag' ? 'active' : ''}`}
                onClick={() => {
                  setViewType('device-tag');
                  setInputValue('');
                }}
                disabled={loading}
              >
                🏷️ Device Tag
              </button>
              <button
                type="button"
                className={`ai-view-option ${viewType === 'aggregate' ? 'active' : ''}`}
                onClick={() => {
                  setViewType('aggregate');
                  setInputValue('');
                }}
                disabled={loading}
              >
                📊 Aggregate
              </button>
            </div>
          </div>

          {/* Conditional Input Field */}
          {viewType !== 'aggregate' && (
            <div className="ai-input-group">
              <label htmlFor="inputValue">
                {viewType === 'device' ? 'System ID:' : 'Device Tag:'}
              </label>
              {viewType === 'device' ? (
                <input
                  type="text"
                  id="inputValue"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Enter System ID"
                  className="ai-device-input"
                  disabled={loading}
                />
              ) : (
                <select
                  id="inputValue"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="ai-device-input"
                  disabled={loading || tagsLoading}
                >
                  <option value="">
                    {tagsLoading ? 'Loading tags...' : availableTags.length === 0 ? 'No device tags found' : 'Select a Device Tag...'}
                  </option>
                  {availableTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Time Range Selector */}
          <div className="ai-time-range-selector">
            <label htmlFor="timeRange">Time Range:</label>
            <select
              id="timeRange"
              value={timeRange}
              onChange={(e) => setTimeRange(Number(e.target.value))}
              className="ai-time-range-dropdown"
              disabled={loading}
            >
              <option value={1}>1 Day</option>
              <option value={7}>7 Days</option>
            </select>
          </div>

          {/* Submit Button */}
          <div className="ai-submit-group">
            <button type="submit" className="ai-submit-btn" disabled={loading}>
              {loading ? 'Loading...' : 'Get Insights'}
            </button>
          </div>
        </form>
      </div>

      {/* Loading Animation */}
      {loading && (
        <div className="ai-loading-container">
          <div className="ai-loading-card">
            <div className="ai-loading-spinner"></div>
            <div className="ai-loading-message">{loadingMessage}</div>
            <div className="ai-loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      )}

      {/* Error Message for initial load */}
      {error && (
        <div className="ai-error-container">
          <div className="ai-error-card">
            <div className="ai-error-icon">⚠️</div>
            <h3>Error Loading Initial Insights</h3>
            <p>{error}</p>
            <button
              className="ai-retry-btn"
              onClick={() => fetchInsightsData()}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Insights Content */}
      {showInsights && !error && (
        <div className="ai-insights-content">
          {/* Sub-Tab Navigation */}
          <div className="ai-tabs">
            <button
              className={`ai-tab ${activeTab === 'data-stall' ? 'active' : ''}`}
              onClick={() => setActiveTab('data-stall')}
              disabled={!dataStallData}
              style={{ opacity: dataStallData ? 1 : 0.5 }}
            >
              ⏸️ Data Stall {dataStallLoading && '⏳'}
            </button>
            <button
              className={`ai-tab ${activeTab === 'peak-rate-ul' ? 'active' : ''}`}
              onClick={() => setActiveTab('peak-rate-ul')}
              disabled={!peakRateULData}
              style={{ opacity: peakRateULData ? 1 : 0.5 }}
            >
              📶 Throughput UL {peakRateULLoading && '⏳'}
            </button>
            <button
              className={`ai-tab ${activeTab === 'peak-rate-dl' ? 'active' : ''}`}
              onClick={() => setActiveTab('peak-rate-dl')}
              disabled={!peakRateDLData}
              style={{ opacity: peakRateDLData ? 1 : 0.5 }}
            >
              📶 Throughput DL {peakRateDLLoading && '⏳'}
            </button>
            <button
              className={`ai-tab ${activeTab === 'thermal' ? 'active' : ''}`}
              onClick={() => setActiveTab('thermal')}
              disabled={!thermalData}
              style={{ opacity: thermalData ? 1 : 0.5 }}
            >
              🌡️ Thermal {thermalLoading && '⏳'}
            </button>
            <button
              className={`ai-tab ${activeTab === 'crashes' ? 'active' : ''}`}
              onClick={() => setActiveTab('crashes')}
              disabled={!crashData}
              style={{ opacity: crashData ? 1 : 0.5 }}
            >
              💥 Crash {crashLoading && '⏳'}
            </button>
          </div>

          {/* Data Stall Dashboard */}
          {activeTab === 'data-stall' && (
            <div className="ai-dashboard active">
              {renderCacheInfo(cacheMetadata.dataStall, deviceCounts.dataStall)}
              {dataStallError ? (
                <div className="ai-error-card">
                  <div className="ai-error-icon">
                    {dataStallError.type === 'warming' ? '⏳' :
                     dataStallError.type === 'not_found' ? '🔍' : '⚠️'}
                  </div>
                  <h3>
                    {dataStallError.type === 'warming' ? 'Generating Data Stall Insights' :
                     dataStallError.type === 'not_found' ? 'Data Stall Data Not Found' :
                     dataStallError.type === 'rate_limit' ? 'Insights Rate Limited' :
                     dataStallError.type === 'unavailable' ? 'Insights Service Unavailable' :
                     dataStallError.type === 'timeout' ? 'Request Timed Out' :
                     'Error Loading Data Stall'}
                  </h3>
                  <p>{dataStallError.message}</p>
                  {dataStallError.warning && (
                    <p className="ai-error-warning">⚠️ {dataStallError.warning}</p>
                  )}
                  {dataStallError.type !== 'warming' && (
                    <button
                      className="ai-retry-btn"
                      onClick={() => fetchDataStall(process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2')}
                    >
                      Retry
                    </button>
                  )}
                </div>
              ) : dataStallData ? (
                <div className="ai-metrics-grid">
                  {renderMetricsCard(dataStallData.metrics_card, '⏸️', 'Data Stall Metrics', 'warning-bg')}
                  {renderExtremesCard(dataStallData.extremes_card)}
                  {renderAnomaliesCard(dataStallData.anomalies_card)}
                  {renderTrendsCard(dataStallData.trends_card)}
                  {renderPatternsCard(dataStallData.patterns_card)}
                  {renderRecoveryHierarchyCard()}
                </div>
              ) : null}
            </div>
          )}

          {/* Throughput UL Dashboard */}
          {activeTab === 'peak-rate-ul' && (
            <div className="ai-dashboard active">
              {renderCacheInfo(cacheMetadata.peakRateUL, deviceCounts.peakRateUL)}
              {peakRateULError ? (
                <div className="ai-error-card">
                  <div className="ai-error-icon">
                    {peakRateULError.type === 'warming' ? '⏳' :
                     peakRateULError.type === 'not_found' ? '🔍' : '⚠️'}
                  </div>
                  <h3>
                    {peakRateULError.type === 'warming' ? 'Generating Throughput UL Insights' :
                     peakRateULError.type === 'not_found' ? 'Throughput UL Data Not Found' :
                     peakRateULError.type === 'rate_limit' ? 'Insights Rate Limited' :
                     peakRateULError.type === 'unavailable' ? 'Insights Service Unavailable' :
                     peakRateULError.type === 'timeout' ? 'Request Timed Out' :
                     'Error Loading Throughput UL'}
                  </h3>
                  <p>{peakRateULError.message}</p>
                  {peakRateULError.warning && (
                    <p className="ai-error-warning">⚠️ {peakRateULError.warning}</p>
                  )}
                  {peakRateULError.type !== 'warming' && (
                    <button
                      className="ai-retry-btn"
                      onClick={() => fetchPeakRateUL(process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2')}
                    >
                      Retry
                    </button>
                  )}
                </div>
              ) : peakRateULData ? (
                <div className="ai-metrics-grid">
                  {renderMetricsCard(peakRateULData.metrics_card, '📶', 'Uplink Rate Metrics', 'primary-bg')}
                  {renderExtremesCard(peakRateULData.extremes_card)}
                  {renderAnomaliesCard(peakRateULData.anomalies_card)}
                  {renderTrendsCard(peakRateULData.trends_card)}
                  {renderPatternsCard(peakRateULData.patterns_card)}
                  {renderBaselineCard(peakRateULData.baseline_card)}
                </div>
              ) : null}
            </div>
          )}

          {/* Throughput DL Dashboard */}
          {activeTab === 'peak-rate-dl' && (
            <div className="ai-dashboard active">
              {renderCacheInfo(cacheMetadata.peakRateDL, deviceCounts.peakRateDL)}
              {peakRateDLError ? (
                <div className="ai-error-card">
                  <div className="ai-error-icon">
                    {peakRateDLError.type === 'warming' ? '⏳' :
                     peakRateDLError.type === 'not_found' ? '🔍' : '⚠️'}
                  </div>
                  <h3>
                    {peakRateDLError.type === 'warming' ? 'Generating Throughput DL Insights' :
                     peakRateDLError.type === 'not_found' ? 'Throughput DL Data Not Found' :
                     peakRateDLError.type === 'rate_limit' ? 'Insights Rate Limited' :
                     peakRateDLError.type === 'unavailable' ? 'Insights Service Unavailable' :
                     peakRateDLError.type === 'timeout' ? 'Request Timed Out' :
                     'Error Loading Throughput DL'}
                  </h3>
                  <p>{peakRateDLError.message}</p>
                  {peakRateDLError.warning && (
                    <p className="ai-error-warning">⚠️ {peakRateDLError.warning}</p>
                  )}
                  {peakRateDLError.type !== 'warming' && (
                    <button
                      className="ai-retry-btn"
                      onClick={() => fetchPeakRateDL(process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2')}
                    >
                      Retry
                    </button>
                  )}
                </div>
              ) : peakRateDLData ? (
                <div className="ai-metrics-grid">
                  {renderMetricsCard(peakRateDLData.metrics_card, '📶', 'Downlink Rate Metrics', 'primary-bg')}
                  {renderExtremesCard(peakRateDLData.extremes_card)}
                  {renderAnomaliesCard(peakRateDLData.anomalies_card)}
                  {renderTrendsCard(peakRateDLData.trends_card)}
                  {renderPatternsCard(peakRateDLData.patterns_card)}
                  {renderBaselineCard(peakRateDLData.baseline_card)}
                </div>
              ) : null}
            </div>
          )}

          {/* Thermal Dashboard */}
          {activeTab === 'thermal' && (
            <div className="ai-dashboard active">
              {renderCacheInfo(cacheMetadata.thermal, deviceCounts.thermal)}
              {thermalError ? (
                <div className="ai-error-card">
                  <div className="ai-error-icon">
                    {thermalError.type === 'warming' ? '⏳' : 
                     thermalError.type === 'not_found' ? '🔍' : '⚠️'}
                  </div>
                  <h3>
                    {thermalError.type === 'warming' ? 'Generating Thermal Insights' :
                     thermalError.type === 'not_found' ? 'Thermal Data Not Found' :
                     thermalError.type === 'rate_limit' ? 'Insights Rate Limited' :
                     thermalError.type === 'unavailable' ? 'Insights Service Unavailable' :
                     thermalError.type === 'timeout' ? 'Request Timed Out' :
                     'Error Loading Thermal'}
                  </h3>
                  <p>{thermalError.message}</p>
                  {thermalError.warning && (
                    <p className="ai-error-warning">⚠️ {thermalError.warning}</p>
                  )}
                  {thermalError.type !== 'warming' && (
                    <button
                      className="ai-retry-btn"
                      onClick={() => fetchThermal(process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2')}
                    >
                      Retry
                    </button>
                  )}
                </div>
              ) : thermalData ? (
                <div className="ai-metrics-grid">
                  {renderMetricsCard(thermalData.metrics_card, '🌡️', 'Temperature Metrics', 'danger-bg')}
                  {renderExtremesCard(thermalData.extremes_card)}
                  {renderAnomaliesCard(thermalData.anomalies_card)}
                  {renderTrendsCard(thermalData.trends_card)}
                  {renderPatternsCard(thermalData.patterns_card)}
                  {renderBaselineCard(thermalData.baseline_card)}
                </div>
              ) : null}
            </div>
          )}

          {/* Crash Dashboard */}
          {activeTab === 'crashes' && (
            <div className="ai-dashboard active">
              {renderCacheInfo(cacheMetadata.crash, deviceCounts.crash)}
              {crashError ? (
                <div className="ai-error-card">
                  <div className="ai-error-icon">
                    {crashError.type === 'warming' ? '⏳' :
                     crashError.type === 'not_found' ? '🔍' : '⚠️'}
                  </div>
                  <div className="ai-error-content">
                    <h3>
                      {crashError.type === 'warming' ? 'Generating Crash Insights' :
                       crashError.type === 'not_found' ? 'Crash Data Not Found' :
                       crashError.type === 'rate_limit' ? 'Insights Rate Limited' :
                       crashError.type === 'unavailable' ? 'Insights Service Unavailable' :
                       crashError.type === 'timeout' ? 'Request Timed Out' :
                       'Error Loading Crash'}
                    </h3>
                    <p>{crashError.message}</p>
                    {crashError.warning && (
                      <p className="ai-error-warning">⚠️ {crashError.warning}</p>
                    )}
                    {crashError.type !== 'warming' && (
                      <button className="ai-retry-btn"
                        onClick={() => fetchCrash(process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2')}
                      >Retry</button>
                    )}
                  </div>
                </div>
              ) : crashData ? (
                <div className="ai-metrics-grid">
                  {renderMetricsCard(crashData.metrics_card, '💥', 'Overview', 'danger-bg')}
                  {renderTrendsCard(crashData.trends_card)}
                  {renderPatternsCard(crashData.patterns_card)}
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AIInsights;
